import { useRef, useState } from 'react';
import { usePaymentsDialog } from '../../payments/paymentsDialogStore';
import { THEMES, usePrefs, useSessionContext, type ThemeId } from '@adiox/ui';
import { ContextMenu, type MenuItem } from '../../chart/ChartOverlays';
import { useChartCommands } from '../../chart/chartCommands';
import { normalizeTemplates } from '../../chart/templates';
import { TOOLS, TOOL_LABELS } from '../../drawings/model';
import { usePrefsValue } from '@adiox/ui';
import { useInstall } from '../install';
import type { PanelEntry } from '../TopBar';

/** What the menus and the main toolbar ask of the workspace. */
export interface ClassicActions {
  newChart: () => void;
  newOrder: () => void;
  listPanels: () => PanelEntry[];
  togglePanel: (id: string) => void;
  toggleToolbox: () => void;
  toolboxOpen: () => boolean;
  /** The charts, to list in the Window menu and switch to. */
  listCharts: () => { id: string; title: string; active: boolean }[];
  activateChart: (id: string) => void;
  openProfiles: () => void;
  openSettings: () => void;
  openShortcuts: () => void;
  openGuide: () => void;
  resetLayout: () => void;
}

const MENUS = ['File', 'View', 'Insert', 'Charts', 'Tools', 'Window', 'Help'] as const;
type MenuName = (typeof MENUS)[number];

/** The menu bar of the classic workspace. Chart commands act on the active chart. */
export function MenuBar({ actions }: { actions: ClassicActions }) {
  const [open, setOpen] = useState<{ name: MenuName; x: number; y: number } | null>(null);
  const closedAt = useRef<{ name: MenuName; at: number } | null>(null);
  const buttons = useRef<Partial<Record<MenuName, HTMLButtonElement | null>>>({});
  const chart = useChartCommands((s) => s.active);
  const { tokens } = useSessionContext();
  const { density, setDensity, themeChoice, setTheme } = usePrefs();
  const install = useInstall();
  const templates = normalizeTemplates(usePrefsValue((s) => s.data.chartTemplates));

  const show = (name: MenuName) => {
    const box = buttons.current[name]!.getBoundingClientRect();
    setOpen({ name, x: box.left, y: box.bottom });
  };
  const c = chart;
  const s = c?.state;
  const panel = (id: string, label: string, shortcut?: string): MenuItem => {
    const entry = actions.listPanels().find((p) => p.id === id);
    return {
      key: id,
      label,
      shortcut,
      checked: entry?.open ?? false,
      onSelect: () => actions.togglePanel(id),
    };
  };

  const items = (name: MenuName): MenuItem[] => {
    switch (name) {
      case 'File':
        return [
          { label: 'New Chart', onSelect: actions.newChart },
          { label: 'Profiles…', onSelect: actions.openProfiles },
          { label: 'Save as Picture…', disabled: !c, onSelect: () => c?.saveImage() },
          { label: 'Open an Account (demo)…', onSelect: actions.openSettings },
          { label: 'Deposit and Withdrawal…', onSelect: () => usePaymentsDialog.getState().show() },
          ...(install.canInstall
            ? [{ label: 'Install as an App', onSelect: () => void install.install() }]
            : []),
          { label: 'Sign Out', danger: true, onSelect: () => void tokens.logout() },
        ];
      case 'View':
        return [
          panel('marketWatch', 'Market Watch', 'Ctrl+M'),
          panel('dataWindow', 'Data Window', 'Ctrl+D'),
          panel('navigator', 'Navigator'),
          {
            key: 'toolbox',
            label: 'Toolbox',
            checked: actions.toolboxOpen(),
            onSelect: actions.toggleToolbox,
          },
          panel('ticks', 'Tick Chart'),
          panel('order', 'New Order window', 'F9'),
          {
            key: 'compact',
            label: 'Compact Rows',
            checked: density === 'compact',
            onSelect: () => setDensity(density === 'compact' ? 'comfortable' : 'compact'),
          },
          {
            key: 'fullscreen',
            label: 'Full Screen',
            shortcut: 'F11',
            checked: typeof document !== 'undefined' && document.fullscreenElement !== null,
            onSelect: () =>
              document.fullscreenElement
                ? void document.exitFullscreen()
                : void document.documentElement.requestFullscreen?.(),
          },
          { key: 'reset', label: 'Reset Layout…', danger: true, onSelect: actions.resetLayout },
        ];
      case 'Insert':
        return [
          { label: 'Indicators…', disabled: !c, onSelect: () => c?.openIndicatorList() },
          ...TOOLS.map((t) => ({
            key: t,
            label: TOOL_LABELS[t],
            disabled: !c,
            checked: s?.tool === t,
            onSelect: () => c?.setTool(t),
          })),
        ];
      case 'Charts':
        return !c || !s
          ? [{ label: 'No chart is open', disabled: true, onSelect: () => undefined }]
          : [
              {
                key: 'bars',
                label: 'Bar Chart',
                shortcut: 'Alt+1',
                checked: s.type === 'bars',
                onSelect: () => c.setType('bars'),
              },
              {
                key: 'candles',
                label: 'Candlesticks',
                shortcut: 'Alt+2',
                checked: s.type === 'candles',
                onSelect: () => c.setType('candles'),
              },
              {
                key: 'line',
                label: 'Line Chart',
                shortcut: 'Alt+3',
                checked: s.type === 'line',
                onSelect: () => c.setType('line'),
              },
              {
                key: 'area',
                label: 'Area Chart',
                checked: s.type === 'area',
                onSelect: () => c.setType('area'),
              },
              { key: 'save-tpl', label: 'Save Template…', onSelect: c.saveTemplate },
              ...Object.keys(templates).map((name) => ({
                key: `tpl-${name}`,
                label: `Template: ${name}`,
                onSelect: () => c.applyTemplate(name),
              })),
              {
                key: 'grid',
                label: 'Grid',
                shortcut: 'Ctrl+G',
                checked: s.grid,
                onSelect: () => c.toggle('grid'),
              },
              {
                key: 'volumes',
                label: 'Volumes',
                shortcut: 'Ctrl+L',
                checked: s.volumes,
                onSelect: () => c.toggle('volumes'),
              },
              {
                key: 'separators',
                label: 'Period Separators',
                shortcut: 'Ctrl+Y',
                checked: s.periodSeparators,
                onSelect: () => c.toggle('periodSeparators'),
              },
              {
                key: 'autoscroll',
                label: 'Auto Scroll',
                checked: s.autoScroll,
                onSelect: () => c.toggle('autoScroll'),
              },
              {
                key: 'shift',
                label: 'Chart Shift',
                checked: s.chartShift,
                onSelect: () => c.toggle('chartShift'),
              },
              {
                key: 'oneclick',
                label: 'One Click Trading',
                shortcut: 'Alt+B / Alt+S',
                checked: s.oneClick,
                onSelect: () => c.toggle('oneClick'),
              },
              { key: 'zoomin', label: 'Zoom In', shortcut: '+', onSelect: () => c.zoom(1) },
              { key: 'zoomout', label: 'Zoom Out', shortcut: '−', onSelect: () => c.zoom(-1) },
              { key: 'step', label: 'Step by Step', shortcut: 'F12', onSelect: c.step },
              { key: 'objects', label: 'Objects List', shortcut: 'Ctrl+B', onSelect: c.openObjects },
              { key: 'popout', label: 'Move to a New Window', onSelect: c.popOut },
              { key: 'props', label: 'Properties', shortcut: 'F8', onSelect: c.openProperties },
            ];
      case 'Tools':
        return [
          { label: 'New Order', shortcut: 'F9', onSelect: actions.newOrder },
          { label: 'Alerts', onSelect: () => actions.togglePanel('alerts') },
          { label: 'History', onSelect: () => actions.togglePanel('history') },
          { label: 'Mailbox', onSelect: () => actions.togglePanel('mailbox') },
          { label: 'Deposit and Withdrawal…', onSelect: () => usePaymentsDialog.getState().show() },
          { label: 'Options…', onSelect: actions.openSettings },
        ];
      case 'Window':
        return [
          { key: 'new', label: 'New Window', onSelect: actions.newChart },
          ...actions.listCharts().map((w, i) => ({
            key: w.id,
            label: `${i + 1} ${w.title}`,
            checked: w.active,
            onSelect: () => actions.activateChart(w.id),
          })),
        ];
      case 'Help':
        return [
          { label: 'Keyboard Shortcuts', shortcut: 'F1', onSelect: actions.openShortcuts },
          { label: 'Welcome Guide', onSelect: actions.openGuide },
        ];
    }
  };

  return (
    <nav className="classic-menubar flex shrink-0 items-center" aria-label="Main menu" data-testid="menu-bar">
      {MENUS.map((name) => (
        <button
          key={name}
          ref={(el) => {
            buttons.current[name] = el;
          }}
          type="button"
          aria-haspopup="menu"
          aria-expanded={open?.name === name}
          className={`classic-menu-title ${open?.name === name ? 'is-open' : ''}`}
          onClick={() => {
            // A click on the title of the menu that is open closes it (the click outside already did).
            const last = closedAt.current;
            if (last?.name === name && Date.now() - last.at < 300) return;
            show(name);
          }}
          onPointerEnter={() => {
            if (open && open.name !== name) show(name);
          }}
        >
          {name}
        </button>
      ))}
      <label className="ml-auto mr-1 flex items-center gap-1 pr-2 text-xs" htmlFor="classic-menubar-theme">
        <span className="sr-only">Theme</span>
        <select
          id="classic-menubar-theme"
          className="classic-menubar-theme"
          value={themeChoice}
          onChange={(e) => setTheme(e.target.value as ThemeId | 'system')}
          title="Theme"
        >
          <option value="system">System</option>
          {(Object.keys(THEMES) as ThemeId[]).map((id) => (
            <option key={id} value={id}>
              {THEMES[id].label}
            </option>
          ))}
        </select>
      </label>
      {open && (
        <ContextMenu
          key={open.name}
          x={open.x}
          y={open.y}
          title={open.name}
          items={items(open.name)}
          onClose={() => {
            closedAt.current = { name: open.name, at: Date.now() };
            setOpen(null);
          }}
        />
      )}
    </nav>
  );
}
